import { Link } from "react-router-dom";
import { ArrowUpRight, Code2, Palette, Share2, TrendingUp, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

const ICONS = { Code2, Palette, Share2, TrendingUp };

export default function Services() {
    return (
        <main className="pt-32 sm:pt-40 pb-24">
            <Seo title="Services" path="/services" description="Web development, graphic design, social media management, and digital marketing services from Innovative Fusion Hives in Long Beach, CA." />
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="services-hero">
                <Reveal>
                    <Eyebrow>Our Services</Eyebrow>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                        Everything your brand needs to thrive online.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
                        Four connected disciplines, delivered by one team. Choose a service to explore deliverables, process, and FAQs — or combine them into a full growth program.
                    </p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-14" data-testid="services-philosophy">
                <Reveal>
                    <div className="max-w-3xl space-y-5">
                        <p className="text-base text-slate-400 leading-relaxed">
                            Most growing businesses do not need four different vendors — they need one partner who understands how the pieces connect. A website that is beautiful but invisible to search engines fails. A social presence without a website that converts wastes attention. Advertising without a landing page built for conversion burns budget. Our four services are designed to work alone or as a single connected growth program, and we will always tell you honestly which combination fits your goals and budget.
                        </p>
                        <p className="text-base text-slate-400 leading-relaxed">
                            Every engagement follows the same six-step process — Discovery, Strategy, Design, Development, Review, Launch — so you always know what happens next, what it costs, and what you will receive. Scopes, deliverables, and timelines are confirmed in writing before work begins, and advertising budgets or third-party platform fees are always quoted separately, never buried in a proposal.
                        </p>
                    </div>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-16 grid gap-6 md:grid-cols-2" data-testid="services-grid">                {SERVICES.map((s, i) => {
                    const Icon = ICONS[s.icon];
                    return (
                        <Reveal key={s.slug} delay={i * 0.08}>
                            <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-hive-card transition-all hover:border-violet-500/50 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(124,58,237,0.15)]">
                                <div className="relative h-48 overflow-hidden">
                                    <img src={s.image} alt={`${s.name} service visual`} loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-hive-card to-transparent" />
                                    <div className="absolute bottom-4 left-6 flex items-center gap-3">
                                        <span className="rounded-lg border border-white/15 bg-black/60 p-2 backdrop-blur"><Icon className="h-5 w-5 text-violet-300" /></span>
                                        <h2 className="font-display text-2xl font-bold text-white">{s.name}</h2>
                                    </div>
                                </div>
                                <div className="flex flex-1 flex-col p-6 sm:p-7">
                                    <p className="text-sm text-slate-400 leading-relaxed">{s.short}</p>
                                    <ul className="mt-5 grid grid-cols-1 gap-2">
                                        {s.overview.slice(0, 4).map((o) => (
                                            <li key={o} className="flex items-start gap-2 text-sm text-slate-300">
                                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {o}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-auto pt-7 flex items-center justify-between gap-4">
                                        <Link to={`/services/${s.slug}`} data-testid={`services-explore-${s.slug}`}
                                            className="inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-violet-200 transition-colors">
                                            Explore service <ArrowUpRight className="h-4 w-4" />
                                        </Link>
                                        <Link to={`/contact?service=${encodeURIComponent(s.name)}`} data-testid={`services-cta-${s.slug}`}
                                            className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-all hover:border-cyan-400/60 hover:text-white">
                                            {s.cta}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    );
                })}
            </section>

            <p className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-10 text-xs text-slate-500 leading-relaxed" data-testid="services-scope-note">
                Website features, integrations, advertising budgets, and third-party platform fees depend on the agreed project scope and are always confirmed in writing before work begins.
            </p>
        </main>
    );
}
